import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AppCatalog from "@/pages/AppCatalog";
import ClientDashboard from "@/pages/ClientDashboard";
import ProcessDocument from "@/pages/ProcessDocument";
import AdminPortal from "@/pages/AdminPortal";
import DownloadPage from "@/pages/DownloadPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios
axios.defaults.withCredentials = true;

// Add response interceptor to handle 401 errors
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      // Only clear localStorage if we're not already on the login page
      // and if this is an authenticated request that failed
      const isAuthRequest = error.config?.headers?.Authorization;
      if (isAuthRequest && window.location.pathname !== "/login" && window.location.pathname !== "/auth/callback") {
        console.log("401 error - clearing auth state");
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Auth Context
export const AuthContext = React.createContext(null);

import React from "react";

// Auth Callback Component - handles Google OAuth
function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get("session_id");

      if (!sessionId) {
        toast.error("Authentication failed");
        navigate("/login");
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/session`, {
          session_id: sessionId
        });

        // Store token and user data
        if (response.data.access_token) {
          localStorage.setItem("token", response.data.access_token);
        }
        localStorage.setItem("user", JSON.stringify({
          user_id: response.data.user_id,
          email: response.data.email,
          name: response.data.name,
          role: response.data.role,
          company_id: response.data.company_id,
          picture: response.data.picture
        }));
        toast.success("Welcome back!");
        
        // Clear hash and navigate based on role
        window.history.replaceState(null, "", window.location.pathname);
        
        if (response.data.role === "admin") {
          navigate("/admin", { state: { user: response.data } });
        } else {
          navigate("/apps", { state: { user: response.data } });
        }
      } catch (error) {
        console.error("Auth error:", error);
        toast.error("Authentication failed");
        navigate("/login");
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children, requireAdmin = false }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user data passed from AuthCallback, use it directly
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      // First check localStorage for JWT auth
      const storedUser = localStorage.getItem("user");
      const token = localStorage.getItem("token");

      if (storedUser && token) {
        const parsed = JSON.parse(storedUser);
        
        // Check admin requirement
        if (requireAdmin && parsed.role !== "admin") {
          navigate("/dashboard");
          return;
        }
        
        setUser(parsed);
        setIsAuthenticated(true);
        return;
      }

      // Then try cookie-based auth (Google OAuth)
      try {
        const response = await axios.get(`${API}/auth/me`);
        
        // Check admin requirement
        if (requireAdmin && response.data.role !== "admin") {
          navigate("/dashboard");
          return;
        }
        
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/login");
      }
    };

    checkAuth();
  }, [navigate, location.state, requireAdmin]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();

  // Check for session_id SYNCHRONOUSLY during render (before useEffect)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/download" element={<DownloadPage />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin={true}>
            <AdminPortal />
          </ProtectedRoute>
        }
      />
      {/* App Catalog - Main client landing page */}
      <Route
        path="/apps"
        element={
          <ProtectedRoute>
            <AppCatalog />
          </ProtectedRoute>
        }
      />
      {/* DocuFlow Extraction App */}
      <Route
        path="/apps/document-processing"
        element={
          <ProtectedRoute>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />
      {/* Legacy route - redirect to app catalog */}
      <Route
        path="/dashboard"
        element={<Navigate to="/apps" replace />}
      />
      <Route
        path="/process/:documentId"
        element={
          <ProtectedRoute>
            <ProcessDocument />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/apps" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
